
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowRight, ArrowUp } from "lucide-react";

interface CompetitorBubble {
  name: string;
  url: string;
  x: number; // 0-100 for accuracy
  y: number; // 0-100 for scale
  size: number; // size of the bubble (1-3)
  highlight?: boolean;
  logoUrl?: string;
  color?: string;
}

const CompetitiveAnalysisChart = () => {
  const competitors: CompetitorBubble[] = [
    { name: "Bing", url: "https://www.bing.com", x: 25, y: 75, size: 1, color: "#4285F4" },
    { name: "Google", url: "https://www.google.com", x: 30, y: 70, size: 1, color: "#EA4335" },
    { name: "IBM", url: "https://www.ibm.com", x: 20, y: 65, size: 1, color: "#0F62FE" },
    { name: "Bloomberg Terminal", url: "https://www.bloomberg.com/professional/solution/bloomberg-terminal", x: 60, y: 15, size: 1, color: "#2800D1" },
    { name: "Zoominfo", url: "https://www.zoominfo.com", x: 70, y: 20, size: 1, color: "#00A8E0" },
    { 
      name: "Benchr", 
      url: "https://www.app.benchr.co", 
      x: 85, 
      y: 80, 
      size: 2, 
      highlight: true,
      logoUrl: "/lovable-uploads/5b3fc61d-09cc-4356-aca7-ef3a45266769.png", // Updated to use the correct Benchr logo
      color: "#1e70d1" // Benchr blue color
    },
  ];

  const getFaviconUrl = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch (error) {
      console.error("Invalid URL:", url);
      return "";
    }
  };

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="pb-2 bg-gradient-to-r from-benchr-100 to-benchr-200 dark:from-benchr-900 dark:to-benchr-800">
        <CardTitle className="text-xl">Web-Wide Crawls</CardTitle>
        <CardDescription className="text-benchr-800 dark:text-benchr-200">
          Competitive analysis of web crawler capabilities based on scale and accuracy
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative h-[500px] w-full bg-gradient-to-br from-white to-benchr-50 dark:from-benchr-950 dark:to-benchr-900 p-6 overflow-hidden">
          {/* Quadrant background styling */}
          <div className="absolute inset-0 z-0">
            <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-green-100/20 dark:bg-green-900/10 border-b border-r border-gray-200 dark:border-gray-700"></div>
            <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-blue-100/20 dark:bg-blue-900/10 border-b border-l border-gray-200 dark:border-gray-700"></div>
            <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-yellow-100/20 dark:bg-yellow-900/10 border-t border-r border-gray-200 dark:border-gray-700"></div>
            <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-purple-100/20 dark:bg-purple-900/10 border-t border-l border-gray-200 dark:border-gray-700"></div>
          </div>
          
          {/* Axis lines */}
          <div className="absolute left-[15%] right-14 top-1/2 h-[2px] bg-gray-300 dark:bg-gray-600 z-10">
            {/* Arrow for the horizontal axis (Accuracy) */}
            <div className="absolute right-0 top-1/2 transform translate-x-1 -translate-y-1/2">
              <ArrowRight size={20} className="text-gray-400 dark:text-gray-400" />
            </div>
          </div>
          <div className="absolute bottom-[15%] top-14 left-1/2 w-[2px] bg-gray-300 dark:bg-gray-600 z-10">
            {/* Arrow for the vertical axis (Scale) */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1">
              <ArrowUp size={20} className="text-gray-400 dark:text-gray-400" />
            </div>
          </div>
          
          {/* Quadrant labels */}
          <div className="absolute top-4 left-4 text-sm font-medium text-gray-600 dark:text-gray-300 z-20 bg-white/80 dark:bg-benchr-900/80 p-2 rounded-md shadow-sm">
            High Scale, Low Accuracy
          </div>
          <div className="absolute top-4 right-4 text-sm font-medium text-gray-600 dark:text-gray-300 z-20 bg-white/80 dark:bg-benchr-900/80 p-2 rounded-md shadow-sm">
            High Scale, High Accuracy
          </div>
          <div className="absolute bottom-4 left-4 text-sm font-medium text-gray-600 dark:text-gray-300 z-20 bg-white/80 dark:bg-benchr-900/80 p-2 rounded-md shadow-sm">
            Low Scale, Low Accuracy
          </div>
          <div className="absolute bottom-4 right-4 text-sm font-medium text-gray-600 dark:text-gray-300 z-20 bg-white/80 dark:bg-benchr-900/80 p-2 rounded-md shadow-sm">
            Low Scale, High Accuracy
          </div>
          
          {/* Axis labels */}
          <div className="absolute bottom-10 right-1/2 transform translate-x-12 text-gray-500 dark:text-gray-400 text-sm font-medium z-20 flex items-center">
            ACCURACY 
          </div>
          <div className="absolute left-1/2 top-1/2 transform -translate-x-24 -translate-y-12 text-gray-500 dark:text-gray-400 text-sm font-medium z-20 flex items-center">
            <span className="rotate-90 origin-center ml-2">SCALE</span>
          </div>
          
          {/* Grid lines */}
          <div className="absolute inset-0 grid grid-cols-4 z-0">
            {[...Array(4)].map((_, i) => (
              <div key={`col-${i}`} className="border-r border-gray-200 dark:border-gray-800/50 h-full"></div>
            ))}
          </div>
          <div className="absolute inset-0 grid grid-rows-4 z-0">
            {[...Array(4)].map((_, i) => (
              <div key={`row-${i}`} className="border-b border-gray-200 dark:border-gray-800/50 w-full"></div>
            ))}
          </div>
          
          {/* Company bubbles */}
          {competitors.map((competitor, index) => (
            <TooltipProvider key={index}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div 
                    className={cn(
                      "absolute flex items-center justify-center rounded-full transition-all duration-500 cursor-pointer hover:shadow-glow z-30",
                      competitor.highlight 
                        ? "bg-gradient-to-br from-white to-benchr-100 dark:from-benchr-700 dark:to-benchr-800 text-benchr-900 dark:text-white shadow-lg" 
                        : "bg-white dark:bg-benchr-800 text-benchr-900 dark:text-white shadow-md hover:scale-110"
                    )}
                    style={{
                      left: `${competitor.x}%`,
                      top: `${100 - competitor.y}%`,
                      width: `${competitor.size * 3 + 2}rem`,
                      height: `${competitor.size * 3 + 2}rem`,
                      transform: 'translate(-50%, -50%)',
                      border: competitor.highlight ? '2px solid #e2e8f0' : '1px solid #e2e8f0',
                      zIndex: competitor.highlight ? 40 : 30,
                      boxShadow: competitor.highlight ? `0 0 20px 0 ${competitor.color}80` : '',
                      animation: competitor.highlight ? 'pulse-glow 3s ease-in-out infinite' : '',
                    }}
                  >
                    <div className="flex flex-col items-center gap-1">
                      {competitor.logoUrl ? (
                        <img 
                          src={competitor.logoUrl} 
                          alt={`${competitor.name} logo`} 
                          className={competitor.name === "Benchr" ? "w-auto h-12 object-contain" : "w-auto h-8 object-contain"}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <img 
                          src={getFaviconUrl(competitor.url)} 
                          alt={`${competitor.name} favicon`} 
                          className="w-6 h-6 rounded-sm object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      )}
                      <span 
                        className={cn(
                          "font-medium whitespace-nowrap",
                          competitor.highlight ? "text-base" : "text-xs",
                        )}
                      >
                        {competitor.name}
                      </span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-white/90 dark:bg-benchr-900/90 backdrop-blur-sm border border-benchr-200 dark:border-benchr-700 shadow-xl">
                  <p className="text-sm font-medium">{competitor.name}</p>
                  <p className="text-xs text-benchr-500 dark:text-benchr-300">{competitor.url}</p>
                  <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="font-semibold">Accuracy:</span> {competitor.x}%
                    </div>
                    <div>
                      <span className="font-semibold">Scale:</span> {competitor.y}%
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
          
          {/* Decorative elements */}
          <div className="absolute right-1/4 bottom-1/4 w-32 h-32 rounded-full bg-benchr-200/10 dark:bg-benchr-600/10 blur-2xl"></div>
          <div className="absolute left-1/4 top-1/3 w-24 h-24 rounded-full bg-benchr-300/10 dark:bg-benchr-500/10 blur-xl"></div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompetitiveAnalysisChart;
